import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Download, Printer, Mail, FileText } from 'lucide-react';
import { formatInIST } from '../utils/datetime';

const InvoicePreview = ({ 
  invoice, 
  onDownload, 
  onPrint, 
  onEmail,
  className = "" 
}) => {
  if (!invoice) {
    return (
      <Card className={className}>
        <CardContent className="p-8 text-center">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Invoice Available</h3>
          <p className="text-gray-500">Invoice will be generated after payment approval.</p>
        </CardContent>
      </Card>
    );
  }

  const formatDate = (dateString) =>
    formatInIST(dateString, { year: 'numeric', month: 'long', day: 'numeric' });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Invoice Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Invoice Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3">
            {onDownload && (
              <Button onClick={onDownload} variant="outline">
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </Button>
            )}
            {onPrint && (
              <Button onClick={onPrint} variant="outline">
                <Printer className="w-4 h-4 mr-2" />
                Print
              </Button>
            )}
            {onEmail && (
              <Button onClick={onEmail} variant="outline">
                <Mail className="w-4 h-4 mr-2" />
                Email Invoice
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Invoice Preview */}
      <Card className="border-2 border-gray-200">
        <CardContent className="p-0">
          {/* Invoice Header */}
          <div className="bg-gray-50 p-6 border-b">
            <div className="flex justify-between items-start">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">INVOICE</h1>
                <p className="text-gray-600 mt-1">Event Registration</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-gray-600">Invoice #</p>
                <p className="font-mono font-medium">{invoice.invoiceNumber || 'INV-001'}</p>
                <p className="text-sm text-gray-600 mt-2">Date</p>
                <p className="font-medium">{formatDate(invoice.invoiceDate || new Date())}</p>
              </div>
            </div>
          </div>

          {/* Invoice Body */}
          <div className="p-6">
            {/* Event and User Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Event Details</h3>
                <div className="space-y-1 text-sm">
                  <p><strong>Event:</strong> {invoice.eventName}</p>
                  <p><strong>Date:</strong> {formatDate(invoice.eventDate)}</p>
                  <p><strong>Location:</strong> {invoice.eventLocation || 'Online'}</p>
                </div>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Bill To</h3>
                <div className="space-y-1 text-sm">
                  <p><strong>Name:</strong> {invoice.userName}</p>
                  <p><strong>Email:</strong> {invoice.userEmail}</p>
                  <p><strong>Registration ID:</strong> {invoice.registrationId}</p>
                </div>
              </div>
            </div>

            {/* Payment Details Table */}
            <div className="mb-8">
              <h3 className="font-semibold text-gray-900 mb-3">Payment Details</h3>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Description</th>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Payment Method</th>
                      <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-t border-gray-200">
                      <td className="px-4 py-3 text-sm text-gray-900">
                        Event Registration Fee
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 capitalize">
                        {invoice.paymentMethod || 'Bank Transfer'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 text-right font-medium">
                        {formatCurrency(invoice.amount)}
                      </td>
                    </tr>
                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td colSpan="2" className="px-4 py-3 text-right text-sm font-medium text-gray-700">
                        Total Amount:
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-bold text-gray-900">
                        {formatCurrency(invoice.amount)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* Payment Status */}
            <div className="mb-8">
              <h3 className="font-semibold text-gray-900 mb-3">Payment Status</h3>
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className="text-sm text-gray-700">
                  Payment received and confirmed on {formatDate(invoice.paymentDate || new Date())}
                </span>
              </div>
            </div>

            {/* QR Code Section */}
            {invoice.qrCodeUrl && (
              <div className="mb-8 text-center">
                <h3 className="font-semibold text-gray-900 mb-3">Registration QR Code</h3>
                <div className="inline-block p-4 border-2 border-gray-200 rounded-lg">
                  <img 
                    src={invoice.qrCodeUrl} 
                    alt="Registration QR Code" 
                    className="w-32 h-32 mx-auto"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Present this QR code at the event for check-in
                </p>
              </div>
            )}

            {/* Footer */}
            <div className="border-t border-gray-200 pt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Thank You!</h4>
                  <p className="text-sm text-gray-600">
                    Thank you for registering for our event. We look forward to seeing you there!
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-600">
                    For any queries, please contact us at:
                  </p>
                  <p className="text-sm font-medium text-gray-900">
                    support@eventreg.com
                  </p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default InvoicePreview;
